import { useMemo } from "react";
import { useIntl } from "react-intl";

import { IconCancelXGrey, IconCheck, IconPrice } from "@/components/Icons";

import type { ExtractedFlightFareDetails } from "../extractDataFromFlightFare";
import {
  FlightFareCardSection,
  FlightFareCardSectionItem,
} from "../FlightFareCardSection";

type FlightFareChangeRefundInfoProps = {
  changeInfo: ExtractedFlightFareDetails["flightChange"] | null;
  refundInfo: ExtractedFlightFareDetails["flightRefund"] | null;
};

export function FlightFareChangeRefundInfo({
  changeInfo,
  refundInfo,
}: FlightFareChangeRefundInfoProps) {
  const intl = useIntl();

  const change = useMemo<{ text: string; icon: React.ReactNode } | null>(() => {
    if (!changeInfo) return null;

    switch (changeInfo.variant) {
      case "changeable":
        return {
          text: intl.formatMessage(
            {
              defaultMessage:
                "Free flight changes {changeTiming, select, before_departure {before departure} after_departure {after departure} same_day {same day} other {}}",
              description:
                "Text indicating that a flight fare allows free flight changes with timing conditions",
            },
            {
              changeTiming: changeInfo.timing,
            },
          ),
          icon: <IconCheck className="size-6" />,
        };

      case "changeable_with_fees":
        return {
          text: intl.formatMessage(
            {
              defaultMessage:
                "Flight changes for a fee {changeTiming, select, before_departure {before departure} after_departure {after departure} same_day {same day} other {}}",
              description:
                "Text indicating that a flight fare allows flight changes for a fee with timing conditions",
            },
            {
              changeTiming: changeInfo.timing,
            },
          ),
          icon: <IconPrice className="size-6" />,
        };

      case "change_not_possible":
        return {
          text: intl.formatMessage(
            {
              defaultMessage:
                "Non-changeable {changeTiming, select, before_departure {before departure} after_departure {after departure} same_day {same day} other {}}",
              description:
                "Text indicating that a flight fare does not allow flight changes with timing conditions",
            },
            {
              changeTiming: changeInfo.timing,
            },
          ),
          icon: <IconCancelXGrey className="size-6" />,
        };

      default:
        return null;
    }
  }, [changeInfo, intl]);

  const refund = useMemo(() => {
    if (!refundInfo) return null;

    switch (refundInfo.variant) {
      case "refundable":
        return {
          text: intl.formatMessage(
            {
              defaultMessage:
                "Fully refundable {refundTiming, select, before_departure {before departure} after_departure {after departure} same_day {same day} other {}}",
              description:
                "Text indicating that a flight fare is refundable without fees with timing conditions",
            },
            {
              refundTiming: refundInfo.timing,
            },
          ),
          icon: <IconCheck className="size-6" />,
        };

      case "refundable_with_fees":
        return {
          text: intl.formatMessage(
            {
              defaultMessage:
                "Refundable for a fee {refundTiming, select, before_departure {before departure} after_departure {after departure} same_day {same day} other {}}",
              description:
                "Text indicating that a flight fare is refundable with fees with timing conditions",
            },
            {
              refundTiming: refundInfo.timing,
            },
          ),
          icon: <IconPrice className="size-6" />,
        };

      case "refundable_as_credit":
        return {
          text: intl.formatMessage(
            {
              defaultMessage:
                "Fully refundable as credit {refundTiming, select, before_departure {before departure} after_departure {after departure} same_day {same day} other {}}",
              description:
                "Text indicating that a flight fare is refundable as credit without fees with timing conditions",
            },
            {
              refundTiming: refundInfo.timing,
            },
          ),
          icon: <IconCheck className="size-6" />,
        };

      case "refundable_as_credit_with_fees":
        return {
          text: intl.formatMessage(
            {
              defaultMessage:
                "Refundable as credit for a fee {refundTiming, select, before_departure {before departure} after_departure {after departure} same_day {same day} other {}}",
              description:
                "Text indicating that a flight fare is refundable as credit with fees with timing conditions",
            },
            {
              refundTiming: refundInfo.timing,
            },
          ),
          icon: <IconPrice className="size-6" />,
        };

      case "non_refundable":
        return {
          text: intl.formatMessage(
            {
              defaultMessage:
                "Non-refundable {refundTiming, select, before_departure {before departure} after_departure {after departure} same_day {same day} other {}}",
              description:
                "Text indicating that a flight fare is non-refundable with timing conditions",
            },
            {
              refundTiming: refundInfo.timing,
            },
          ),
          icon: <IconCancelXGrey className="size-6" />,
        };

      default:
        return null;
    }
  }, [refundInfo, intl]);

  return (
    <FlightFareCardSection
      title={intl.formatMessage({
        defaultMessage: "Cancellation & change",
        description:
          "Section title for cancellation and change information in fare card",
      })}
    >
      {!!change && (
        <FlightFareCardSectionItem label={change.text} icon={change.icon} />
      )}

      {!!refund && (
        <FlightFareCardSectionItem label={refund.text} icon={refund.icon} />
      )}
    </FlightFareCardSection>
  );
}
